import { useEffect, useState } from "react";
import http from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Trash2, Plus } from "lucide-react";

const TYPES = [["diagnostics", "Diagnostikk"], ["labour", "Arbeid"], ["part", "Deler"], ["transport", "Transport"], ["discount", "Rabatt"]];
const inp = "bg-slate-900 border border-slate-600 rounded px-2 py-1 text-sm text-white w-full";

export default function AdminBillingSection({ orderId, initialBilling }) {
  const [b, setB] = useState({
    pricing_mode: initialBilling?.pricing_mode || "standard",
    line_items: initialBilling?.line_items || [],
    invoice_status: initialBilling?.invoice_status || "ikke_utstedt",
    due_date: initialBilling?.due_date || "",
    final_total_incl_mva: initialBilling?.final_total_incl_mva || 0,
    payment_status: initialBilling?.payment_status || "ubetalt",
    amount_paid: initialBilling?.amount_paid || 0,
  });
  const [purchases, setPurchases] = useState([]);
  const [pay, setPay] = useState({ amount: "", method: "" });
  const [pur, setPur] = useState({ item: "", supplier: "", purchase_price_nok: "", shipping_cost_nok: "", tracking_number: "", carrier: "", status: "bestilt", eta_days: "", client_visible_note: "Reservedel bestilt", order_ref: "" });

  const loadPurchases = () => http.get(`/v1/admin/repair-orders/${orderId}/purchases`).then((r) => setPurchases(r.data)).catch(() => {});
  useEffect(() => { if (orderId) loadPurchases(); /* eslint-disable-next-line */ }, [orderId]);

  const total = b.line_items.reduce((s, li) => s + (parseFloat(li.amount_incl_mva) || 0), 0);
  const addLine = () => setB({ ...b, line_items: [...b.line_items, { label: "", type: "labour", amount_incl_mva: 0 }] });
  const setLine = (i, k, v) => setB({ ...b, line_items: b.line_items.map((li, idx) => (idx === i ? { ...li, [k]: v } : li)) });
  const delLine = (i) => setB({ ...b, line_items: b.line_items.filter((_, idx) => idx !== i) });

  const saveBilling = async () => {
    try {
      const payload = {
        pricing_mode: b.pricing_mode, invoice_status: b.invoice_status, due_date: b.due_date || null,
        line_items: b.line_items.map((li) => ({ ...li, amount_incl_mva: parseFloat(li.amount_incl_mva) || 0, hours: li.hours ? parseFloat(li.hours) : null, rate_incl_mva: li.rate_incl_mva ? parseFloat(li.rate_incl_mva) : null })),
      };
      const { data } = await http.put(`/v1/admin/repair-orders/${orderId}/billing`, payload);
      setB({ ...b, ...data });
      toast.success("Økonomi lagret");
    } catch { toast.error("Feil ved lagring"); }
  };
  const addPayment = async () => {
    if (!pay.amount) return;
    try {
      const { data } = await http.post(`/v1/admin/repair-orders/${orderId}/payment`, { amount: parseFloat(pay.amount), method: pay.method });
      setB({ ...b, ...data }); setPay({ amount: "", method: "" }); toast.success("Betaling registrert");
    } catch { toast.error("Feil"); }
  };
  const addPurchase = async () => {
    if (!pur.item) { toast.error("Angi vare"); return; }
    try {
      await http.post(`/v1/admin/repair-orders/${orderId}/purchases`, { ...pur, purchase_price_nok: parseFloat(pur.purchase_price_nok) || 0, shipping_cost_nok: parseFloat(pur.shipping_cost_nok) || 0, eta_days: pur.eta_days === "" ? null : parseInt(pur.eta_days, 10) });
      setPur({ item: "", supplier: "", purchase_price_nok: "", shipping_cost_nok: "", tracking_number: "", carrier: "", status: "bestilt", eta_days: "", client_visible_note: "Reservedel bestilt", order_ref: "" });
      loadPurchases(); toast.success("Innkjøp lagt til");
    } catch { toast.error("Feil"); }
  };
  const delPurchase = async (id) => { try { await http.delete(`/v1/admin/repair-orders/${orderId}/purchases/${id}`); loadPurchases(); } catch { /* ignore */ } };
  const updPurchaseStatus = async (p, status) => {
    try { await http.put(`/v1/admin/repair-orders/${orderId}/purchases/${p.id}`, { ...p, status, eta_days: p.eta_days ?? null }); loadPurchases(); toast.success("Status oppdatert"); }
    catch { toast.error("Feil ved oppdatering"); }
  };

  const margin = total - purchases.reduce((s, p) => s + (p.purchase_price_nok || 0) + (p.shipping_cost_nok || 0), 0);
  const badge = (txt, cls) => <span className={`text-xs px-2 py-0.5 rounded ${cls}`}>{txt}</span>;

  return (
    <div className="space-y-4" data-testid="billing-section">
      {/* Prisrader */}
      <div className="flex items-center justify-between">
        <span className="text-xs text-slate-400">Prismodell</span>
        <select value={b.pricing_mode} onChange={(e) => setB({ ...b, pricing_mode: e.target.value })} className={`${inp} w-40`} data-testid="billing-mode">
          <option value="standard">Standard (A/B/C)</option>
          <option value="individual">Individuell (mester)</option>
        </select>
      </div>
      <div className="space-y-2">
        {b.line_items.map((li, i) => (
          <div key={i} className="grid grid-cols-12 gap-1 items-center" data-testid={`billing-line-${i}`}>
            <input className={`${inp} col-span-4`} placeholder="Beskrivelse" value={li.label} onChange={(e) => setLine(i, "label", e.target.value)} />
            <select className={`${inp} col-span-2`} value={li.type} onChange={(e) => setLine(i, "type", e.target.value)}>{TYPES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}</select>
            <input className={`${inp} col-span-2`} type="number" placeholder="Timer" value={li.hours ?? ""} onChange={(e) => setLine(i, "hours", e.target.value)} title="Antall/timer (valgfritt)" />
            <input className={`${inp} col-span-3`} type="number" placeholder="Beløp inkl. mva" value={li.amount_incl_mva} onChange={(e) => setLine(i, "amount_incl_mva", e.target.value)} data-testid={`billing-amount-${i}`} />
            <button onClick={() => delLine(i)} className="col-span-1 text-red-400 hover:text-red-300 flex justify-center" data-testid={`billing-del-${i}`}><Trash2 className="h-4 w-4" /></button>
          </div>
        ))}
        <Button type="button" variant="ghost" size="sm" onClick={addLine} className="text-blue-400 hover:text-blue-300" data-testid="billing-add-line"><Plus className="h-4 w-4 mr-1" />Legg til rad</Button>
      </div>
      <div className="flex items-center justify-between text-sm">
        <span className="text-slate-400">Totalt (inkl. 25% mva)</span>
        <span className="font-bold text-white" data-testid="billing-total">{total.toLocaleString("nb-NO")} kr</span>
      </div>

      {/* Faktura + betaling */}
      <div className="grid grid-cols-2 gap-2">
        <div>
          <label className="text-xs text-slate-400">Faktura</label>
          <select value={b.invoice_status} onChange={(e) => setB({ ...b, invoice_status: e.target.value })} className={inp} data-testid="billing-invoice-status">
            <option value="ikke_utstedt">Ikke utstedt</option>
            <option value="utstedt">Utstedt</option>
          </select>
        </div>
        <div>
          <label className="text-xs text-slate-400">Forfallsdato</label>
          <input type="date" value={b.due_date || ""} onChange={(e) => setB({ ...b, due_date: e.target.value })} className={inp} data-testid="billing-due" />
        </div>
      </div>
      <Button type="button" onClick={saveBilling} className="bg-blue-600 hover:bg-blue-700 text-white w-full" data-testid="billing-save">Lagre økonomi</Button>

      <div className="flex items-center gap-2 text-sm">
        <span className="text-slate-400">Betaling:</span>
        {b.payment_status === "betalt" ? badge("Betalt", "bg-green-900/50 text-green-300") : b.payment_status === "delvis" ? badge("Delvis betalt", "bg-amber-900/50 text-amber-300") : badge("Ubetalt", "bg-red-900/50 text-red-300")}
        <span className="text-slate-400 ml-auto">Betalt: {(b.amount_paid || 0).toLocaleString("nb-NO")} kr</span>
      </div>
      <div className="grid grid-cols-12 gap-1">
        <input className={`${inp} col-span-5`} type="number" placeholder="Beløp" value={pay.amount} onChange={(e) => setPay({ ...pay, amount: e.target.value })} data-testid="payment-amount" />
        <input className={`${inp} col-span-4`} placeholder="Metode (vipps/kort)" value={pay.method} onChange={(e) => setPay({ ...pay, method: e.target.value })} />
        <Button type="button" onClick={addPayment} className="col-span-3 bg-green-700 hover:bg-green-600 text-white" data-testid="payment-add">Reg. betaling</Button>
      </div>
      <p className="text-[11px] text-slate-500">Betaling registreres kun ved bekreftet beløp – ikke automatisk.</p>

      {/* Innkjøp / interne kostnader */}
      <div className="border-t border-slate-700 pt-3">
        <p className="text-xs font-semibold text-slate-300 mb-1">Innkjøp / deler-bestilling (kun internt – pris/leverandør ikke synlig for kunde)</p>
        {purchases.length > 0 && (
          <div className="space-y-1 mb-2" data-testid="purchase-list">
            {purchases.map((p) => (
              <div key={p.id} className="bg-slate-900/50 rounded px-2 py-1.5 text-xs text-slate-300">
                <div className="flex items-center justify-between">
                  <span>{p.item} · {p.supplier || "–"} · {(p.purchase_price_nok || 0)}+{(p.shipping_cost_nok || 0)} kr</span>
                  <button onClick={() => delPurchase(p.id)} className="text-red-400 hover:text-red-300" data-testid={`purchase-del-${p.id}`}><Trash2 className="h-3.5 w-3.5" /></button>
                </div>
                <div className="flex flex-wrap items-center gap-2 mt-1">
                  <span className="text-slate-400">{p.carrier || "ingen transportør"} · {p.tracking_number || "ingen tracking"}{p.eta_days != null ? ` · ~${p.eta_days} dager` : ""}</span>
                  <select value={p.status || "bestilt"} onChange={(e) => updPurchaseStatus(p, e.target.value)} className="bg-slate-700 border border-slate-600 text-slate-200 rounded px-1.5 py-0.5 text-xs" data-testid={`purchase-status-${p.id}`}>
                    <option value="bestilt">Bestilt</option>
                    <option value="sendt">Sendt</option>
                    <option value="under_transport">Under transport</option>
                    <option value="levert_til_verksted">Levert til verksted</option>
                  </select>
                </div>
              </div>
            ))}
          </div>
        )}
        <div className="grid grid-cols-12 gap-1">
          <input className={`${inp} col-span-4`} placeholder="Vare" value={pur.item} onChange={(e) => setPur({ ...pur, item: e.target.value })} data-testid="purchase-item" />
          <input className={`${inp} col-span-3`} placeholder="Leverandør" value={pur.supplier} onChange={(e) => setPur({ ...pur, supplier: e.target.value })} />
          <input className={`${inp} col-span-2`} type="number" placeholder="Pris" value={pur.purchase_price_nok} onChange={(e) => setPur({ ...pur, purchase_price_nok: e.target.value })} />
          <input className={`${inp} col-span-3`} type="number" placeholder="Frakt" value={pur.shipping_cost_nok} onChange={(e) => setPur({ ...pur, shipping_cost_nok: e.target.value })} />
          <select className={`${inp} col-span-3`} value={pur.carrier} onChange={(e) => setPur({ ...pur, carrier: e.target.value })} data-testid="purchase-carrier">
            <option value="">Transportør…</option>
            <option value="bring">Bring</option>
            <option value="posten">Posten</option>
            <option value="postnord">PostNord</option>
            <option value="dhl">DHL</option>
            <option value="ups">UPS</option>
            <option value="fedex">FedEx</option>
            <option value="schenker">Schenker</option>
          </select>
          <input className={`${inp} col-span-4`} placeholder="Tracking (kan vises kunde)" value={pur.tracking_number} onChange={(e) => setPur({ ...pur, tracking_number: e.target.value })} data-testid="purchase-tracking" />
          <input className={`${inp} col-span-2`} type="number" placeholder="ETA dg" value={pur.eta_days} onChange={(e) => setPur({ ...pur, eta_days: e.target.value })} data-testid="purchase-eta" />
          <input className={`${inp} col-span-3`} placeholder="Kundetekst" value={pur.client_visible_note} onChange={(e) => setPur({ ...pur, client_visible_note: e.target.value })} />
          <Button type="button" onClick={addPurchase} className="col-span-12 bg-slate-600 hover:bg-slate-500 text-white" data-testid="purchase-add"><Plus className="h-4 w-4 mr-1" />Legg til deler-bestilling</Button>
        </div>
        <p className="text-[11px] text-slate-500 mt-1">ETA er et estimat (ikke et løfte). Status oppdateres manuelt eller via transportørens sporing. Kunden ser aldri pris/leverandør.</p>
        <div className="flex justify-between text-xs text-slate-400 mt-2">
          <span>Margin (internt): kunde − deler − frakt</span>
          <span className={margin >= 0 ? "text-green-400" : "text-red-400"} data-testid="billing-margin">{margin.toLocaleString("nb-NO")} kr</span>
        </div>
      </div>
    </div>
  );
}
